<template>
   <div class="container-fluid playground bg-light-purple">
      <NotFound v-if="!playgroundData" />
      <div v-if="playgroundData" class="content">
         <!-- HEADER -->
         <div class="row">
            <div class="col-12 title mt-2">
               <h1 class="header-container">
                  {{ playgroundData.header }}
               </h1>
            </div>
            <div v-if="collapsibleContent" class="col-12">
               <div class="description-container-gallery" v-html="collapsibleContent" />
            </div>
         </div>

         <!-- CHART VIEW -->
         <div class="row">
            <div class="col-12">
               <ChartView
                  :html-code="playgroundData.code.htmlCode"
                  :js-code="playgroundData.code.jsCode"
                  :css-code="playgroundData.code.cssCode"
                  :seo-img-url="playgroundData.preview"
                  :seo-img-alt="playgroundData.header"
                  :show-themes="true"
                  :product="playgroundData.product"
                  :live-example-btn="playgroundData.liveExample"
                  @themechanged="selectTheme"
               />
               <h2 v-if="playgroundData.shortDescription" class="mt-2 mb-2">
                  {{ descriptionJoiner(playgroundData.shortDescription) }}
               </h2>

               <a v-if="prevLink" :href="prevLink" class="prev-slide-btn" @click.prevent="goPrev()">
                  <i class="fc_keyboard_arrow_left" />
               </a>
               <a v-if="nextLink" :href="nextLink" class="next-slide-btn" @click.prevent="goNext()">
                  <i class="fc_keyboard_arrow_right" />
               </a>
            </div>
         </div>

         <!-- CODE VIEW -->
         <div v-if="isClient && !playgroundData.liveExample" class="row">
            <mq-layout class="code-view-wrapper col-12" :mq="['xs', 'sm', 'md', 'navigation']">
               <CodeViewSm
                  ref="sliderTabPositionMethodsm"
                  :display="playgroundData.code.display"
                  :slug="$route.params.slug"
                  :is-time-series="true"
                  :is-static="playgroundData.isStatic"
                  :selected-theme="selectedTheme"
                  :code="playgroundData.code"
                  @frameworkSelected="updateFrameworkName"
               />
            </mq-layout>
            <mq-layout class="code-view-wrapper col-12" mq="lg+">
               <CodeView
                  ref="sliderTabPositionMethod"
                  :display="playgroundData.code.display"
                  :slug="$route.params.slug"
                  :is-time-series="true"
                  :is-static="playgroundData.isStatic"
                  :selected-theme="selectedTheme"
                  :code-details="codeViewDetails"
                  :code="playgroundData.code"
                  @frameworkSelected="updateFrameworkName"
               />
            </mq-layout>
         </div>
         <div class="row">
            <div v-if="playgroundData.pageContent" class="col-12">
               <div
                  class="description-container-gallery1"
                  v-html="markedRender(playgroundData.pageContent.longDescription)"
               />
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { mapState } from 'vuex';
import NotFound from '../NotFound.vue';
import languages from '../../components/playground/languages.js';
import metaGenerator from '../../util/meta.js';
import { disposeAll } from '../../util/chart';

export default {
   name: 'FusionTimeDetails',

   components: {
      ChartView: () =>
         import(/* webpackChunkName: "playground" */ '../../components/playground/ChartView'),
      CodeView: () =>
         import(/* webpackChunkName: "playground" */ '../../components/playground/CodeView'),
      CodeViewSm: () =>
         import(/* webpackChunkName: "playground" */ '../../components/playground/CodeViewSm'),
      NotFound,
   },

   data() {
      return {
         languages,
         isClient: false,
         selectedTheme: null,
         isCollapsed: true,
         nextLink: null,
         prevLink: null,
         userSelectedFramework: null,
         ...(process.browser && { mixpanelFusionTimeTracker: this.$tracker.fusionTime }),
      };
   },

   computed: {
      ...mapState(['playgroundData', 'playgroundSidebarData']),

      collapsibleContent() {
         return this.markedRender(this.playgroundData.longDescription);
      },

      codeViewDetails() {
         return {
            title: this.playgroundData.meta.header,
            description: this.playgroundData.meta.description,
         };
      },
      filteredLanguages() {
         return this.languages.filter(language => language.fusionTimeAbility);
      },
   },

   beforeRouteUpdate(to, from, next) {
      next();
      this.initNavLinks();
   },
   watch: {
      $route(to, from) {
         this.updateRoute();
         const el = this.$refs.sliderTabPositionMethod || this.$refs.sliderTabPositionMethodsm;
         el.slideTabPosition();
      },
   },

   asyncData({ store, route }) {
      if (typeof window !== 'undefined') {
         return Promise.all([
            store.dispatch('FUSIONTIME_DETAILS_FETCH', {
               slug: route.params.slug,
               language: store.state.ftUserSelectedFramework,
            }),
         ]);
      }

      return Promise.all([
         store.dispatch('FUSIONTIME_DETAILS_FETCH', {
            slug: route.params.slug,
            language: 'js',
         }),
      ]);
   },

   destroyed() {
      disposeAll();
   },

   created() {
      this.initNavLinks();
   },
   beforeMount() {
      const frameworks = this.filteredLanguages.map(el => el.value);
      const lang =
         frameworks.indexOf(this.$getUserSelectedFramework()) > -1
            ? this.$getUserSelectedFramework()
            : 'js';
      this.$set(this.$store.state, 'ftUserSelectedFramework', lang);
   },

   mounted() {
      this.isClient = true;
      this.mixpanelFusionTimeTracker.visited();
      this.userSelectedFramework =
         this.$getUserSelectedFramework() === (false || 'js')
            ? 'javascript'
            : this.$getUserSelectedFramework();
      this.updateRoute();
   },

   methods: {
      updateFrameworkName(frameworkName) {
         if (frameworkName) {
            this.userSelectedFramework = frameworkName === 'js' ? 'javascript' : frameworkName;
            this.updateRoute();
         }
      },

      updateRoute() {
         !this.playgroundData.isStatic &&
            this.$router
               .push({
                  query: {
                     ...this.$route.query,
                     framework: this.userSelectedFramework || 'javascript',
                  },
               })
               .catch(() => {});
      },

      selectTheme(theme) {
         this.selectedTheme = theme;
      },
      goNext() {
         this.$bar.start();
         this.$router.push(this.nextLink, this.$bar.finish);
      },
      goPrev() {
         this.$bar.start();
         this.$router.push(this.prevLink, this.$bar.finish);
      },

      initNavLinks() {
         this.nextLink = null;
         this.prevLink = null;

         if (!this.playgroundSidebarData) return;

         const currentPath = this.$route.path;
         let hitItem = false;

         function extractLinks({ items }, parentItems, rootIdx) {
            if (!items) return false;
            if (hitItem) return true;
            let prevLink;
            let nextLink;

            for (let idx = 0; idx < items.length; idx += 1) {
               const subItem = items[idx];
               if (subItem.link === currentPath) {
                  if (idx > 0) {
                     prevLink = items[idx - 1].link;
                  } else if (rootIdx > 0) {
                     const prevList = parentItems[rootIdx - 1].items;
                     if (prevList && prevList.length > 0) {
                        prevLink = prevList[prevList.length - 1].link;
                     }
                  }

                  if (idx < items.length - 1) {
                     nextLink = items[idx + 1].link;
                  } else if (rootIdx < parentItems.length - 1) {
                     const nextList = parentItems[rootIdx + 1].items;
                     if (nextList && nextList.length > 0) {
                        nextLink = nextList[0].link;
                     }
                  }

                  hitItem = true;
                  break;
               }

               if (subItem.items && subItem.items.length > 0) {
                  if (!hitItem) {
                     [prevLink, nextLink] = extractLinks(subItem, items, idx);
                  } else break;
               }
            }

            return [prevLink, nextLink];
         }

         const chartGalleryItems = this.playgroundSidebarData[0].items[1].items;
         for (let index = 0; index < chartGalleryItems.length; index += 1) {
            const subItem = chartGalleryItems[index];
            if (!hitItem) {
               [this.prevLink, this.nextLink] = extractLinks(subItem, chartGalleryItems, index);
            } else break;
         }
      },
   },

   metaInfo() {
      return metaGenerator(this.playgroundData);
   },
};
</script>

<style lang="scss">
@import '../../util';

.playground {
   overflow: hidden;

   .code-view-wrapper {
      width: 100%;
   }

   .next-slide-btn,
   .prev-slide-btn {
      position: absolute;
      top: 40%;
      display: flex;
      padding: 10px;
      background-color: #fff;
      color: #000;
      box-shadow: 0 4px 9px 0 rgba(23, 28, 33, 0.12);
      border-radius: 50%;
      transform: scale(0.9);
      text-decoration: none;

      &:hover {
         text-decoration: none;
      }

      @include media-breakpoint-up(sm) {
         transform: scale(1);
      }

      i {
         position: relative;
         top: 1px;
      }
   }

   .next-slide-btn {
      right: 5px;

      @include media-breakpoint-up(sm) {
         right: -8px;
      }
   }

   .prev-slide-btn {
      left: 5px;

      @include media-breakpoint-up(sm) {
         left: -8px;
      }
   }
}

.content {
   padding: 30px 0;

   @include media-breakpoint-up(sm) {
      padding: 30px;
   }

   @include media-breakpoint-up(md) {
      padding: 50px 30px 30px 30px;
   }

   @include media-breakpoint-up(lg) {
      padding: 50px 25px 50px 25px;
   }

   @include media-breakpoint-up(xl) {
      padding: 50px 30px 50px 30px;
   }
}

.playground-header {
   margin-bottom: 32px;

   .title {
      display: flex;
      flex-flow: column nowrap;
      align-items: flex-start;

      @include media-breakpoint-up(sm) {
         flex-flow: row nowrap;
         align-items: baseline;
      }

      span {
         font-size: 12px;
         display: inline-block;
         vertical-align: middle;
         letter-spacing: 0.2px;
         line-height: 15px;
         color: #212344;
         border: 1px solid rgba(62, 57, 107, 0.13);
         border-radius: 2px;
         background-color: rgba(62, 57, 107, 0.04);

         @include media-breakpoint-up(sm) {
            // margin-left: 10px;
         }
      }
   }

   .description-container-gallery {
      color: #313131;
      font-size: 16px;
      letter-spacing: 0.2px;
      line-height: 28px;
      overflow: hidden;
      max-height: 85px;
      will-change: max-height;
      // padding-top: 15px;

      p:nth-child(2) {
         margin-bottom: 1rem;
      }
   }

   .description-transition {
      transition: 0.8s max-height ease-in-out;
   }

   .btn-read-more {
      display: inline-block;
      color: #8297a2;
      text-transform: uppercase;
      cursor: pointer;

      &:hover,
      &:active {
         color: darken(#8297a2, 15%);
      }
   }
}
.table-boder,
.table-boder th,
.table-boder td {
   border: 2px solid black;
}
.maxImage {
   text-align: center;
   img {
      max-width: 100%;
      text-align: center;
   }
}
</style>
